import api from '../client';

// Normalise une erreur axios en Error enrichie (status HTTP + message lisible),
// pour que l'UI puisse distinguer un 429 (rate limit) d'une vraie erreur.
const toError = (error) => {
  const e = new Error(
    error.response?.data?.error?.message ||
      error.response?.data?.message ||
      error.message ||
      'Erreur réseau'
  );
  e.status = error.response?.status;
  e.data = error.response?.data;
  return e;
};

/**
 * Service pour la connexion d'un compte WhatsApp via Swift AI.
 * Endpoints sous le namespace /api/v1/whatsflow/sessions (préfixe /api/v1 géré par
 * le client). Le frontend ne parle JAMAIS directement à WhatsFlow.
 */
const sessionService = {
  /**
   * Démarrer une connexion WhatsApp par numéro de téléphone (code d'appairage).
   * @param {Object} payload - { session_label, phone_number }
   * @returns {Promise} { session_id, status, phone_number }
   */
  connectByPhone: async ({ session_label, phone_number }) => {
    try {
      const response = await api.post('/whatsflow/sessions/connect-by-phone', {
        session_label,
        phone_number,
      });
      return response.data;
    } catch (error) {
      throw toError(error);
    }
  },

  /**
   * Récupérer le code d'appairage d'une session.
   * @param {string} sessionId
   * @returns {Promise} { status: 'starting'|'pairing_ready'|'connected', code, expires_in }
   */
  getPairingCode: async (sessionId) => {
    try {
      const response = await api.get(`/whatsflow/sessions/${sessionId}/pairing-code`);
      return response.data;
    } catch (error) {
      throw toError(error);
    }
  },

  /**
   * Vérifier l'état de connexion d'une session (relais WhatsFlow tel quel).
   * @param {string} sessionId
   * @returns {Promise} objet de statut (ex: { connected: boolean })
   */
  getSessionStatus: async (sessionId) => {
    try {
      const response = await api.get(`/whatsflow/sessions/${sessionId}/status`);
      return response.data;
    } catch (error) {
      throw toError(error);
    }
  },

  /**
   * Récupérer le QR code frais depuis le bridge Baileys.
   * Retourne { status: 'qr_ready'|'starting'|'connected', qr_code, expires_in }.
   * Appeler toutes les 3 s jusqu'à status='connected'.
   * @param {string} sessionId
   */
  getQR: async (sessionId) => {
    try {
      const response = await api.get(`/whatsflow/session/${sessionId}/qr`);
      return response.data;
    } catch (error) {
      throw toError(error);
    }
  },

  /**
   * Relancer le cycle de connexion d'une session non connectée.
   * Génère un nouveau QR code côté Baileys. Interdit sur une session connectée (409).
   * @param {string} sessionId
   */
  restartSession: async (sessionId) => {
    try {
      const response = await api.post(`/whatsflow/session/${sessionId}/restart`);
      return response.data;
    } catch (error) {
      throw toError(error);
    }
  },
};

export default sessionService;
